/**
 * Checkout Component
 * Handles the checkout process including payment integration with Stripe
 * 
 * @module components/Checkout
 * @requires react
 * @requires react-router-dom
 * @requires @stripe/react-stripe-js
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '../context/CartContext';
import api from '../api';
import './Checkout.css';

/**
 * Checkout component that handles payment processing
 * @returns {JSX.Element} Checkout page with payment form
 */
const Checkout = () => {
  const { fetchCart } = useCart(); // from CartContext.js
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [succeeded, setSucceeded] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [clientSecret, setClientSecret] = useState('');
  
  // Customer information
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US'
    }
  });
  
  // Shipping information (could be the same as billing)
  const [shippingDetails, setShippingDetails] = useState({
    name: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US'
    }
  });
  
  // Use same address for shipping
  const [sameAddress, setSameAddress] = useState(true);

  // Load cart data
  useEffect(() => {
    const loadCart = async () => {
      try {
        const response = await api.get('/api/cart');
        console.log('Cart data received:', response.data);
        console.log('Cart items:', response.data.items); // Add this line
        console.log('First item price:', response.data.items[0]?.price); // Add this line
        setCart(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching cart:', err.response?.data || err.message);
        setError('Error loading cart. Please try again.');
        setLoading(false);
      }
    };
    loadCart();
}, []);

  // Create payment intent
  useEffect(() => {
    // Don't create payment intent if cart is not loaded or stripe is not available
    if (!cart || !stripe || clientSecret) return; // Add clientSecret check to prevent multiple calls
    
    const createPaymentIntent = async () => {
      setClientSecret(''); // Add this line to clear old client secret
      try {
        const response = await api.post('/api/payments/create-intent', {
          billingDetails,
          shippingDetails: sameAddress ? billingDetails : shippingDetails
        });
        
        console.log('Payment intent created:', response.data);
        
        // Add validation for clientSecret
        if (!response.data.clientSecret) {
          console.error('No clientSecret received from backend');
          setError('Payment setup failed. No client secret received.');
          return;
        }
        
        console.log('Setting clientSecret:', response.data.clientSecret);
        setClientSecret(response.data.clientSecret);
      } catch (err) {
        console.error('Error creating payment intent:', err.response?.data || err.message);
        console.error('Full error response:', err.response);
        setError('Error preparing checkout. Please try again.');
      }
    };

    createPaymentIntent();
  }, [cart, stripe, clientSecret, sameAddress, billingDetails, shippingDetails]);

  // Handle billing details change
  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      // Handle nested properties like 'address.line1'
      const [parent, child] = name.split('.');
      setBillingDetails(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      // Handle top-level properties like 'name' or 'email'
      setBillingDetails(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Update shipping details if using same address
    if (sameAddress) {
      setShippingDetails(prev => ({
        ...prev,
        name: name === 'name' ? value : prev.name,
        address: name.includes('address.') ? {
          ...prev.address,
          [name.split('.')[1]]: value
        } : prev.address
      }));
    }
  };

  // Handle shipping details change
  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      // Handle nested properties
      const [parent, child] = name.split('.');
      setShippingDetails(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      // Handle top-level properties
      setShippingDetails(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle checkbox for same address
  const handleSameAddressChange = (e) => {
    setSameAddress(e.target.checked);
    
    if (e.target.checked) {
      // Copy billing address to shipping
      setShippingDetails(prev => ({
        ...prev,
        name: billingDetails.name,
        address: { ...billingDetails.address }
      }));
    }
  };

  // Handle changes in the CardElement
  const handleCardChange = (event) => {
    // Listen for changes in the CardElement
    // and display any errors as the customer types their card details
    setDisabled(event.empty);
    setError(event.error ? event.error.message : '');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded
      return;
    }
    
    // Basic validation
    if (!billingDetails.name || !billingDetails.email || !billingDetails.address.line1) {
      setError('Please fill in all required fields');
      return;
    }
    
    setProcessing(true);

    if (!clientSecret) {
      setError("Can't process payment: no client secret found");
      setProcessing(false);
      return;
    }

       // Get the mounted CardElement safely
    const card = elements.getElement(CardElement);
    if (!card) {
      setError('Payment form is not ready yet. Please try again.');
      setProcessing(false);
      return;
    }

    console.log("Calling confirmCardPayment with:", clientSecret);

    
    // Confirm card payment
    console.log("Confirming payment with clientSecret:", clientSecret);

    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card,
        billing_details: billingDetails
      },
      shipping: sameAddress ? {
        name: billingDetails.name,
        address: billingDetails.address
      } : {
        name: shippingDetails.name,
        address: shippingDetails.address
      }
    });
    
    if (result.error) {
      console.error('Stripe error details:', result.error);
      
      // If it's an unexpected state error, clear the client secret to force a new payment intent
      if (result.error.code === 'payment_intent_unexpected_state') {
        console.log('Payment intent in unexpected state, will create new one');
        // setClientSecret('');
        setError('Payment failed. Please try again.');
      } else {
        setError(`Payment failed: ${result.error.message}`);
      }
      setProcessing(false);
    } else {
      if (result.paymentIntent.status === 'succeeded') {
        // Payment succeeded, notify the server
        try {
          const response = await api.post('/api/payments/success', {
            paymentIntentId: result.paymentIntent.id,
          });
          
          // Payment processed successfully
          setError(null);
          setSucceeded(true);
          setProcessing(false);

            // Optional: instant visual clear (this only clears the local checkout page state)
          setCart({ items: [] });

         // Server has cleared the cart—refresh client-side state so UI is empty
          try {
            await fetchCart(); // pulls the now-empty cart from backend
          } catch (e) {
            // non-fatal; we still redirect
            console.warn('Cart refresh after payment failed:', e);
          }
          
          // Redirect to order confirmation
          navigate(`/order-confirmation/${response.data.orderId}`);
        } catch (err) {
          console.error('Error processing payment success:', err.response?.data || err.message);
          setError('Payment succeeded but we encountered an error processing your order.');
          setProcessing(false);
        }
      }
    }
  };

  // Show loading indicator while cart is being fetched
  if (loading) return <div className="loading">Loading checkout...</div>;
  
  // Show error message if cart couldn't be loaded
  if (error && !cart) return <div className="error">{error}</div>;
  
  // Show error if cart not found
  if (!cart) return <div className="error">Cart not found</div>;

  // Calculate cart total
  const cartTotal = Array.isArray(cart?.items)
  ? cart.items.reduce((total, item) => total + (item.price * item.quantity), 0)
  : 0;


  return (
    <div className="checkout-page">
      <h1>Checkout</h1>
      
      <div className="checkout-container">
        <div className="order-summary">
          <h2>Order Summary</h2>
          
          {Array.isArray(cart.items) && cart.items.map((item) => (
            <div key={item._id || item.product} className="cart-item">
              <div className="item-details">
                <h3>{item.name}</h3>
                <div className="item-price">
                  <span>${item.price.toFixed(2)}</span>
                  <span>Qty: {item.quantity}</span>
                </div>
              </div>
              <div className="item-total">
                ${(item.price * item.quantity).toFixed(2)}
              </div>
            </div>
          ))}
          
          <div className="cart-total">
            <span>Total</span>
            <span>${cartTotal.toFixed(2)}</span>
          </div>
        </div>
        
        <form className="checkout-form" onSubmit={handleSubmit}>
          <h2>Billing Information</h2>
          
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              id="name"
              type="text"
              name="name"
              value={billingDetails.name}
              onChange={handleBillingChange}
              placeholder="Full name"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              name="email"
              value={billingDetails.email}
              onChange={handleBillingChange}
              placeholder="Email address"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="address.line1">Address</label>
            <input
              id="address.line1"
              type="text"
              name="address.line1"
              value={billingDetails.address.line1}
              onChange={handleBillingChange}
              placeholder="Street address"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="address.line2">Address Line 2 (Optional)</label>
            <input
              id="address.line2"
              type="text"
              name="address.line2"
              value={billingDetails.address.line2}
              onChange={handleBillingChange}
              placeholder="Apartment, suite, etc."
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="address.city">City</label>
              <input
                id="address.city"
                type="text"
                name="address.city"
                value={billingDetails.address.city}
                onChange={handleBillingChange}
                placeholder="City"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="address.state">State</label>
              <input
                id="address.state"
                type="text"
                name="address.state"
                value={billingDetails.address.state}
                onChange={handleBillingChange}
                placeholder="State"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="address.postal_code">ZIP Code</label>
              <input
                id="address.postal_code"
                type="text"
                name="address.postal_code"
                value={billingDetails.address.postal_code}
                onChange={handleBillingChange}
                placeholder="ZIP Code"
                required
              />
            </div>
          </div>
          
          <div className="form-group checkbox">
            <input
              id="same-address"
              type="checkbox"
              checked={sameAddress}
              onChange={handleSameAddressChange}
            />
            <label htmlFor="same-address">Shipping address same as billing</label>
          </div>
          
          {!sameAddress && (
            <>
              <h2>Shipping Information</h2>
              
              <div className="form-group">
                <label htmlFor="shipping-name">Name</label>
                <input
                  id="shipping-name"
                  type="text"
                  name="name"
                  value={shippingDetails.name}
                  onChange={handleShippingChange}
                  placeholder="Full name"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="shipping-address.line1">Address</label>
                <input
                  id="shipping-address.line1"
                  type="text"
                  name="address.line1"
                  value={shippingDetails.address.line1}
                  onChange={handleShippingChange}
                  placeholder="Street address"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="shipping-address.line2">Address Line 2 (Optional)</label>
                <input
                  id="shipping-address.line2"
                  type="text"
                  name="address.line2"
                  value={shippingDetails.address.line2}
                  onChange={handleShippingChange}
                  placeholder="Apartment, suite, etc."
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="shipping-address.city">City</label>
                  <input
                    id="shipping-address.city"
                    type="text"
                    name="address.city"
                    value={shippingDetails.address.city}
                    onChange={handleShippingChange}
                    placeholder="City"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="shipping-address.state">State</label>
                  <input
                    id="shipping-address.state"
                    type="text"
                    name="address.state"
                    value={shippingDetails.address.state}
                    onChange={handleShippingChange}
                    placeholder="State"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="shipping-address.postal_code">ZIP Code</label>
                  <input
                    id="shipping-address.postal_code"
                    type="text"
                    name="address.postal_code"
                    value={shippingDetails.address.postal_code}
                    onChange={handleShippingChange}
                    placeholder="ZIP Code"
                    required
                  />
                </div>
              </div>
            </>
          )}
          
          <h2>Payment Information</h2>
          
          <div className="form-group">
            <label htmlFor="card-element">Credit or Debit Card</label>
            <div className="card-element-container">
              <CardElement 
                id="card-element" 
                onChange={handleCardChange}
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                    invalid: {
                      color: '#9e2146',
                    },
                  },
                }}
              />
            </div>
          </div>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <button
            type="submit"
            disabled={processing || disabled || succeeded || !clientSecret || !stripe}
            className="pay-button"
          >
            {processing ? 'Processing...' : `Pay $${(cartTotal).toFixed(2)}`}
          </button>
          
          {succeeded && (
            <div className="success-message">
              Payment successful! Redirecting to order confirmation...
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Checkout;